import { type Action, combineReducers } from 'redux';
import Immutable from 'immutable';

import StoreORM from 'js/src/data/store-orm';
import {
    endLexicalControllingItsOwnState,
    startLexicalControllingItsOwnState,
} from 'js/src/data/transcript/transcript-actions';
import {
    clearActiveTranslationSceneGroupId,
    removeTranslatedEditor,
    storeActiveTranslationSceneGroupId,
    storeTranslatedEditor,
    storeTranslatedEditors,
} from 'js/src/data/translated-editors/translated-editors-actions';
import type {
    TranscriptTranslationsType,
    TranslatedEditorsType,
} from 'js/src/data/translated-editors/translated-editors-types';

export function translated_editors(
    state = StoreORM.newFromIdPrefix<TranslatedEditorsType, string>('translated-editors').data,
    action: Action,
) {
    if (storeTranslatedEditors.match(action)) {
        const orm = new StoreORM<TranslatedEditorsType, string>(state);
        const { sceneGroupId } = action.payload;
        if (orm.getByIdOrUndefined(sceneGroupId)) {
            orm.updateById(sceneGroupId, {
                value: Immutable.fromJS(action.payload.translations),
                key: ['translations'],
            });
        } else {
            orm.pushJSItem(action.payload, 'sceneGroupId');
        }
        return orm.data;
    } else if (storeTranslatedEditor.match(action)) {
        const orm = new StoreORM<TranslatedEditorsType, string>(state);
        const { sceneGroupId, editor, langCode, lexicalControlsItsOwnState } = action.payload;
        const translations = {
            [langCode]: {
                editor,
                lexicalControlsItsOwnState,
            },
        } as TranscriptTranslationsType;

        if (!orm.getByIdOrUndefined(sceneGroupId)) {
            orm.pushJSItem(
                {
                    sceneGroupId,
                    translations,
                },
                'sceneGroupId',
            );
        } else {
            orm.updateById(sceneGroupId, {
                value: Immutable.fromJS(translations),
                key: ['translations'],
            });
        }

        return orm.data;
    } else if (removeTranslatedEditor.match(action)) {
        const orm = new StoreORM<TranslatedEditorsType, string>(state);
        const { sceneGroupId } = action.payload;
        orm.delete([sceneGroupId]);
        return orm.data;
    } else if (startLexicalControllingItsOwnState.match(action)) {
        const orm = new StoreORM<TranslatedEditorsType, string>(state);
        const langCode = action.payload.langCode;
        if (!langCode) {
            // this redux action is meant for the "main" transcript, not the translated transcript
            // so we don't need to do anything here
            return state;
        }

        orm.updateById(action.payload.sceneGroupId, {
            value: true,
            key: ['translations', langCode, 'lexicalControlsItsOwnState'],
        });
        return orm.data;
    } else if (endLexicalControllingItsOwnState.match(action)) {
        const orm = new StoreORM<TranslatedEditorsType, string>(state);
        const langCode = action.payload.langCode;
        if (!langCode) {
            // this redux action is meant for the "main" transcript, not the translated transcript
            // so we don't need to do anything here
            return state;
        }

        orm.updateById(action.payload.sceneGroupId, {
            value: false,
            key: ['translations', langCode, 'lexicalControlsItsOwnState'],
        });
        return orm.data;
    }

    return state;
}

export function active_translation_scene_group_id(state = '', action: Action) {
    if (storeActiveTranslationSceneGroupId.match(action)) {
        return action.payload.sceneGroupId;
    } else if (clearActiveTranslationSceneGroupId.match(action)) {
        return '';
    }

    return state;
}

export const transcript_translations = combineReducers({
    translated_editors,
    active_translation_scene_group_id,
});

export type TranslatedEditorsState = ReturnType<typeof transcript_translations>;
